<template>
  <div class="chat-container">
    <div class="chat-header">
      <div class="chat-contact">
        <Avatar 
          :image="contact.profilePicture ? contact.profilePicture : null" 
          :label="getAvatarInitials(contact.name || contact.number)" 
          size="large" 
          shape="circle" 
          :style="{ backgroundColor: getAvatarColor(contact.number || '') }"
        />
        <div class="contact-info">
          <h3>{{ contact.name || formatPhoneNumber(contact.number) }}</h3>
          <span class="contact-status">{{ contact.online ? 'Online' : 'Offline' }}</span>
        </div>
      </div>
      <div class="chat-actions">
        <Button icon="pi pi-refresh" class="p-button-rounded p-button-text" @click="refreshMessages" />
      </div>
    </div>
    
    <div class="chat-messages" ref="messagesContainer">
      <div v-if="loading" class="loading-messages">
        <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
        <p>Carregando mensagens...</p>
      </div>
      
      <div v-else-if="messages.length === 0" class="no-messages">
        <i class="pi pi-comments" style="font-size: 3rem; color: var(--text-color-secondary)"></i>
        <p>Não há mensagens nesta conversa</p>
        <small>Envie uma mensagem para iniciar a conversa</small>
      </div>
      
      <template v-else>
        <div v-for="(message, index) in messages" :key="message.id || index" class="message-wrapper">
          <div v-if="showMessageDate(index)" class="message-date">
            {{ formatMessageDate(message.timestamp) }}
          </div>
          <div class="message" :class="{ 'sent': message.fromMe, 'received': !message.fromMe }">
            <div class="message-content">
              <div v-if="message.type === 'image'" class="message-media">
                <img :src="message.content" alt="Image" @click="openMedia(message)" />
              </div>
              <div v-else-if="message.type === 'video'" class="message-media">
                <video controls>
                  <source :src="message.content" type="video/mp4">
                  Seu navegador não suporta vídeos.
                </video>
              </div>
              <div v-else-if="message.type === 'document'" class="message-document">
                <i class="pi pi-file-pdf document-icon"></i>
                <span>{{ message.filename || 'Documento' }}</span>
                <Button icon="pi pi-download" class="p-button-text p-button-sm" @click="downloadMedia(message)" />
              </div>
              <div v-else-if="message.type === 'audio'" class="message-audio">
                <audio controls>
                  <source :src="message.content" type="audio/mp3">
                  Seu navegador não suporta áudio.
                </audio>
              </div>
              <div v-else class="message-text">
                {{ message.content }}
              </div>
              <div class="message-time">
                {{ formatMessageTime(message.timestamp) }}
                <i v-if="message.fromMe" :class="getStatusIcon(message.status)" class="message-status-icon"></i>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
    
    <div class="chat-input">
      <div class="message-attachments" v-if="attachments.length > 0">
        <div v-for="(file, index) in attachments" :key="index" class="attachment-preview">
          <div class="attachment-remove">
            <Button icon="pi pi-times" class="p-button-rounded p-button-text p-button-sm" @click="removeAttachment(index)" />
          </div>
          <img v-if="file.type.startsWith('image/')" :src="file.preview" alt="Attachment" />
          <div v-else class="file-preview">
            <i class="pi pi-file"></i>
            <span>{{ file.name }}</span>
          </div>
        </div>
      </div>
      
      <div class="input-container">
        <span class="p-input-icon-left p-input-icon-right">
          <i class="pi pi-paperclip attachment-icon" @click="openAttachmentDialog"></i>
          <InputText 
            v-model="newMessage" 
            placeholder="Digite uma mensagem" 
            @keydown.enter="sendTextMessage" 
            class="message-input"
          />
          <i class="pi pi-smile emoji-icon" @click="toggleEmojiPicker"></i>
        </span>
        <Button 
          icon="pi pi-send" 
          class="p-button-rounded send-button" 
          :disabled="!canSendMessage" 
          @click="sendTextMessage" 
        />
      </div>
      
      <input 
        type="file" 
        ref="fileInput" 
        style="display: none" 
        @change="handleFileSelect" 
        multiple
      />
      
      <div v-if="showEmojiPicker" class="emoji-picker-container">
        <!-- Aqui seria integrado um componente de picker de emojis -->
        <div class="emoji-grid">
          <!-- Lista de emojis simplificada -->
          <button 
            v-for="emoji in commonEmojis" 
            :key="emoji" 
            class="emoji-button" 
            @click="addEmoji(emoji)"
          >
            {{ emoji }}
          </button>
        </div>
      </div>
    </div>
    
    <Dialog 
      v-model:visible="mediaPreviewVisible" 
      :style="{width: '80vw'}" 
      :modal="true" 
      :closable="true" 
      :dismissableMask="true"
    >
      <template #header>
        <h3>Visualização de Mídia</h3>
      </template>
      <div class="media-preview-container">
        <img v-if="previewMedia && previewMedia.type === 'image'" :src="previewMedia.content" alt="Media Preview" class="media-preview" />
        <video v-else-if="previewMedia && previewMedia.type === 'video'" controls class="media-preview">
          <source :src="previewMedia.content" type="video/mp4">
          Seu navegador não suporta vídeos.
        </video>
      </div>
    </Dialog>
  </div>
</template>

<script>
import { ref, onMounted, watch, nextTick, computed } from 'vue';

export default {
  name: 'ChatMessages',
  props: {
    instanceId: {
      type: [String, Number],
      required: true
    },
    contact: {
      type: Object,
      required: true,
      default: () => ({
        number: '',
        name: '',
        profilePicture: null,
        online: false
      })
    },
    messages: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  emits: ['send-message', 'send-media', 'refresh'],
  setup(props, { emit }) {
    const newMessage = ref('');
    const messagesContainer = ref(null);
    const attachments = ref([]);
    const fileInput = ref(null);
    const showEmojiPicker = ref(false);
    const mediaPreviewVisible = ref(false);
    const previewMedia = ref(null);
    
    const commonEmojis = [
      '😀', '😂', '😊', '😍', '🤔', '😴', '😎', '👍', '👋', '🙏',
      '❤️', '🎉', '🔥', '⭐', '🎂', '🎁', '🏆', '✅', '⚠️', '❌'
    ];
    
    const canSendMessage = computed(() => {
      return (newMessage.value.trim() !== '' || attachments.value.length > 0);
    });
    
    const formatPhoneNumber = (number) => {
      if (!number) return '';
      
      // Remove qualquer coisa que não seja número
      const cleaned = number.replace(/\D/g, '');
      
      // Formato brasileiro: (XX) XXXXX-XXXX ou +XX (XX) XXXXX-XXXX
      if (cleaned.length === 11) {
        return `(${cleaned.substring(0, 2)}) ${cleaned.substring(2, 7)}-${cleaned.substring(7)}`;
      } else if (cleaned.length === 13) {
        return `+${cleaned.substring(0, 2)} (${cleaned.substring(2, 4)}) ${cleaned.substring(4, 9)}-${cleaned.substring(9)}`;
      }
      
      return number;
    };
    
    const getAvatarInitials = (name) => {
      if (!name) return '?';
      return name.split(' ')
        .map(word => word.charAt(0).toUpperCase())
        .slice(0, 2)
        .join('');
    };
    
    const getAvatarColor = (text) => {
      if (!text) return '#4caf50';
      
      let hash = 0;
      for (let i = 0; i < text.length; i++) {
        hash = text.charCodeAt(i) + ((hash << 5) - hash);
      }
      
      let color = '#';
      for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xFF;
        color += ('00' + value.toString(16)).substr(-2);
      }
      return color;
    };
    
    const formatMessageTime = (timestamp) => {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    };
    
    const formatMessageDate = (timestamp) => {
      if (!timestamp) return '';
      
      const date = new Date(timestamp);
      const today = new Date();
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      
      if (date.toDateString() === today.toDateString()) {
        return 'Hoje';
      } else if (date.toDateString() === yesterday.toDateString()) {
        return 'Ontem';
      } else {
        return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
      }
    };
    
    const showMessageDate = (index) => {
      if (index === 0) return true;
      
      const currentDate = new Date(props.messages[index].timestamp);
      const previousDate = new Date(props.messages[index - 1].timestamp);
      
      return currentDate.toDateString() !== previousDate.toDateString();
    };
    
    const getStatusIcon = (status) => {
      const statusIcons = {
        'pending': 'pi pi-clock',
        'sent': 'pi pi-check',
        'delivered': 'pi pi-check-circle',
        'read': 'pi pi-check-circle',
        'failed': 'pi pi-times'
      };
      
      return statusIcons[status] || 'pi pi-clock';
    };
    
    const scrollToBottom = () => {
      nextTick(() => {
        if (messagesContainer.value) {
          messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
        }
      });
    };
    
    const sendTextMessage = () => {
      if (!canSendMessage.value) return;
      
      if (attachments.value.length > 0) {
        sendMediaMessages();
        return;
      }
      
      if (newMessage.value.trim() !== '') {
        emit('send-message', {
          instanceId: props.instanceId,
          destination: props.contact.number,
          message: newMessage.value.trim(),
          options: {}
        });
        
        newMessage.value = '';
      }
    };
    
    const openAttachmentDialog = () => {
      fileInput.value.click();
    };
    
    const handleFileSelect = (event) => {
      const files = Array.from(event.target.files);
      
      files.forEach(file => {
        const reader = new FileReader();
        
        reader.onload = (e) => {
          attachments.value.push({
            file,
            type: file.type,
            name: file.name,
            preview: file.type.startsWith('image/') ? e.target.result : null,
            content: e.target.result
          });
        };
        
        if (file.type.startsWith('image/')) {
          reader.readAsDataURL(file);
        } else {
          reader.readAsArrayBuffer(file);
        }
      });
      
      // Limpa o input para permitir selecionar o mesmo arquivo novamente
      event.target.value = '';
    };
    
    const removeAttachment = (index) => {
      attachments.value.splice(index, 1);
    };
    
    const sendMediaMessages = async () => {
      for (const attachment of attachments.value) {
        await emit('send-media', {
          instanceId: props.instanceId,
          destination: props.contact.number,
          file: attachment.file,
          caption: newMessage.value.trim(),
          options: {}
        });
      }
      
      attachments.value = [];
      newMessage.value = '';
    };
    
    const toggleEmojiPicker = () => {
      showEmojiPicker.value = !showEmojiPicker.value;
    };
    
    const addEmoji = (emoji) => {
      newMessage.value += emoji;
      showEmojiPicker.value = false;
    };
    
    const refreshMessages = () => {
      emit('refresh', { 
        instanceId: props.instanceId, 
        chatId: props.contact.number 
      });
    };
    
    const openMedia = (media) => {
      previewMedia.value = media;
      mediaPreviewVisible.value = true;
    };
    
    const downloadMedia = (media) => {
      if (!media.content) return;
      
      const link = document.createElement('a');
      link.href = media.content;
      link.download = media.filename || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };
    
    // Scroll para o final da conversa quando as mensagens mudarem
    watch(() => props.messages, () => {
      scrollToBottom();
    }, { deep: true });
    
    // Scroll para o final na montagem do componente
    onMounted(() => {
      scrollToBottom();
    });
    
    return {
      newMessage,
      messagesContainer,
      attachments,
      fileInput,
      showEmojiPicker,
      commonEmojis,
      mediaPreviewVisible,
      previewMedia,
      canSendMessage,
      formatPhoneNumber,
      getAvatarInitials,
      getAvatarColor,
      formatMessageTime,
      formatMessageDate,
      showMessageDate,
      getStatusIcon,
      sendTextMessage,
      openAttachmentDialog,
      handleFileSelect,
      removeAttachment,
      toggleEmojiPicker,
      addEmoji,
      refreshMessages,
      openMedia,
      downloadMedia
    };
  }
};
</script>

<style scoped>
.chat-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: var(--ink);
  border-radius: 0;
  overflow: hidden;
}

.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: var(--ink-soft);
  padding: 1rem;
  border-bottom: 1px solid var(--line);
}

.chat-contact {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.contact-info {
  display: flex;
  flex-direction: column;
}

.contact-info h3 {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--text);
}

.contact-status {
  font-size: 0.875rem;
  color: var(--muted);
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  background-color: var(--ink);
}

.loading-messages, .no-messages {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--muted);
  text-align: center;
  gap: 1rem;
}

.message-wrapper {
  margin-bottom: 0.5rem;
}

.message-date {
  text-align: center;
  margin: 1rem 0;
  font-size: 0.875rem;
  background-color: transparent;
  padding: 0.3rem 1rem;
  border-radius: 0;
  display: inline-block;
  margin-left: auto;
  margin-right: auto;
  width: auto;
  border-top: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
  color: var(--muted);
  font-family: 'IBM Plex Mono', ui-monospace, monospace;
  font-weight: 500;
}

.message {
  max-width: 70%;
  padding: 0.75rem 1rem;
  border-radius: 0;
  margin-bottom: 0.5rem;
  position: relative;
  word-break: break-word;
}

.message.sent {
  background-color: var(--ink-soft);
  border: 1px solid var(--line);
  margin-left: auto;
}

.message.received {
  background-color: var(--ink);
  border: 1px solid var(--line);
  margin-right: auto;
}

.message-content {
  display: flex;
  flex-direction: column;
}

.message-text {
  color: var(--text);
  line-height: 1.45;
  font-family: 'IBM Plex Sans', system-ui, sans-serif;
  font-weight: 400;
}

.message-time {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.25rem;
  font-size: 0.75rem;
  color: var(--muted);
  margin-top: 0.25rem;
  font-family: 'IBM Plex Mono', ui-monospace, monospace;
  font-weight: 500;
}

.message-status-icon {
  font-size: 0.75rem;
}

.message-media img, .message-media video {
  max-width: 100%;
  max-height: 200px;
  border-radius: 0;
  cursor: pointer;
}

.message-audio audio {
  width: 100%;
  margin-top: 0.5rem;
}

.message-document {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background-color: transparent;
  border-radius: 0;
  border: 1px solid var(--line);
}

.document-icon {
  font-size: 1.5rem;
  color: var(--lime);
}

.chat-input {
  background-color: var(--ink-soft);
  padding: 1rem;
  border-top: 1px solid var(--line);
  position: relative;
}

.input-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.input-container span {
  flex: 1;
}

.message-input {
  width: 100%;
  padding-left: 2.5rem !important;
  padding-right: 2.5rem !important;
}

.attachment-icon, .emoji-icon {
  cursor: pointer;
  color: var(--muted);
}

.attachment-icon:hover, .emoji-icon:hover {
  color: var(--lime);
}

.send-button {
  background-color: var(--lime);
  border: 2px solid var(--text);
}

.emoji-picker-container {
  position: absolute;
  bottom: 100%;
  right: 1rem;
  background-color: var(--ink-soft);
  border-radius: 0;
  border: 1px solid var(--line);
  padding: 1rem;
  z-index: 1000;
  width: 250px;
  max-height: 300px;
  overflow-y: auto;
}

.emoji-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 0.5rem;
}

.emoji-button {
  background: none;
  border: 1px solid transparent;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0;
  transition: background-color 0.2s;
}

.emoji-button:hover {
  background-color: var(--ink);
  border-color: var(--line);
}

.message-attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.attachment-preview {
  width: 80px;
  height: 80px;
  border-radius: 0;
  overflow: hidden;
  position: relative;
  background-color: var(--ink);
  border: 1px solid var(--line);
}

.attachment-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.file-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 0.5rem;
  font-size: 0.75rem;
  text-align: center;
  color: var(--text);
}

.file-preview i {
  font-size: 1.5rem;
  margin-bottom: 0.25rem;
}

.attachment-remove {
  position: absolute;
  top: 0;
  right: 0;
  background-color: var(--lime);
  border-radius: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
}

.media-preview-container {
  display: flex;
  justify-content: center;
  align-items: center;
  max-height: 80vh;
  overflow: auto;
}

.media-preview {
  max-width: 100%;
  max-height: 80vh;
}
</style> 